"use client";

import React, { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "next/navigation";
import InstructorCard from "@/components/common/InstructorCard2";
import PageHeaderWithStats from "@/components/common/PageHeaderWithStats";
import {
  MagnifyingGlassIcon,
  SparklesIcon,
  AcademicCapIcon,
  UserGroupIcon,
  FunnelIcon,
  ArrowRightIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import { expertCategories } from "@/config/expert-categories";
import { useInstructorsWithFilters } from "@/hooks/useInstructorsWithFilters";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";
import { useDebounce } from "@/hooks/useDebounce";
import { Instructor } from "@/types";
import { Footer } from "@/components/Footer";

const InstructorsPage: React.FC = () => {
  const searchParams = useSearchParams();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Handle URL parameters and sessionStorage for category selection
  useEffect(() => {
    const categoryFromUrl = searchParams.get("category");
    const categoryFromStorage = sessionStorage.getItem("selectedCategory");

    console.log("URL params:", { categoryFromUrl, categoryFromStorage });

    if (categoryFromUrl) {
      // Validate category exists in expertCategories
      const validCategory = expertCategories.find(
        (cat) => cat.id === categoryFromUrl
      );
      if (validCategory) {
        setSelectedCategory(categoryFromUrl);
        console.log("Set category from URL:", categoryFromUrl);
      }
      // Clear sessionStorage if URL param is used
      sessionStorage.removeItem("selectedCategory");
    } else if (categoryFromStorage) {
      // Use sessionStorage as fallback
      const validCategory = expertCategories.find(
        (cat) => cat.id === categoryFromStorage
      );
      if (validCategory) {
        setSelectedCategory(categoryFromStorage);
        console.log("Set category from storage:", categoryFromStorage);
      }
      // Clear after using
      sessionStorage.removeItem("selectedCategory");
    }
  }, [searchParams]);

  const selectedCategoryObj = expertCategories.find(
    (c) => c.id === selectedCategory
  );

  const {
    data: infiniteData,
    isLoading: loading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInstructorsWithFilters({
    limit: 12,
    expertType: selectedCategoryObj?.apiFilter || "",
    status: "active,approved",
  });

  // Flatten all pages into a single array
  const instructors = useMemo(() => {
    return infiniteData?.pages.flatMap((page) => page.instructors) || [];
  }, [infiniteData]);

  const totalInstructors = infiniteData?.pages[0]?.pagination?.total || 0;

  // Get unique specialties from instructor data (using instructorType)
  const specialties = Array.from(
    new Set(
      instructors.flatMap((instructor) => instructor.instructorType || [])
    )
  );

  // Calculate enhanced stats
  // Calculate per-instructor class counts and total available classes
  const totalAvailableClasses = instructors.reduce(
    (acc, curr) => acc + (curr.activeClassCount || 0),
    0
  );

  const stats = {
    totalInstructors: totalInstructors,
    averageExperience:
      instructors.length > 0
        ? Math.round(
            (instructors.reduce(
              (acc, curr) => acc + (curr.experience || 0),
              0
            ) /
              instructors.length) *
              10
          ) / 10
        : 0,
    totalSpecialties: specialties.length,
    averageRating:
      instructors.length > 0
        ? Math.round(
            (instructors.reduce(
              (acc, curr) => acc + (curr.stats?.averageRating || 0),
              0
            ) /
              instructors.length) *
              10
          ) / 10
        : 0,
    availableClasses: totalAvailableClasses,
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value.toLowerCase());
  };

  const applyFilters = (instructor: Instructor) => {
    // Basic search filter with debounced search term
    const matchesSearch =
      instructor.name.toLowerCase().includes(debouncedSearchTerm) ||
      (instructor.instructorType || []).some((s: string) =>
        s.toLowerCase().includes(debouncedSearchTerm)
      ) ||
      (instructor.bio || "").toLowerCase().includes(debouncedSearchTerm);

    // Category filter - already handled by React Query
    return matchesSearch;
  };

  // Use filtered instructors directly (no sorting)
  const filteredInstructors = instructors.filter(applyFilters);

  // Infinite scroll - fetch next page when scrolling
  const loadMoreRef = useInfiniteScroll(
    () => {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    { hasMore: hasNextPage || false, isLoading: isFetchingNextPage }
  );

  const resetAllFilters = () => {
    setSearchTerm("");
    setSelectedCategory("all");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center zoom-container">
        <LoadingSpinner
          fullScreen={false}
          showText={true}
          text="Loading instructors..."
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white mt-[60px] sm:mt-[80px]">
      {/* Header and Stats Section */}
      <PageHeaderWithStats
        badge={{ text: "Expert Instructors", showPulse: true }}
        title={{ main: "Find Your Perfect", highlighted: "Guide" }}
        description="Connect with experienced yoga instructors who can guide you on your wellness journey"
        stats={[
          {
            icon: UserGroupIcon,
            value: stats.totalInstructors,
            label: "Expert Instructors",
          },
          {
            icon: AcademicCapIcon,
            value: `${stats.averageExperience}Y`,
            label: "Avg Experience",
          },
          {
            icon: SparklesIcon,
            value: stats.totalSpecialties,
            label: "Specialties",
          },
        ]}
        loading={loading}
      />

      {/* Search and Filter Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 mb-8 sm:mb-12 zoom-container">
        <div className="flex flex-col lg:flex-row gap-3 sm:gap-4 items-center justify-between">
          {/* Search Bar */}
          <div className="relative w-full flex-1 ">
            <div className="relative">
              <input
                type="text"
                placeholder="Search instructors, specialties..."
                value={searchTerm}
                onChange={handleSearch}
                className="w-full px-4 sm:px-6 py-3 sm:py-4 pl-10 sm:pl-14 pr-12 sm:pr-16 rounded-xl sm:rounded-2xl border-2 border-gray-200 bg-white/90 backdrop-blur-sm text-gray-700 placeholder-gray-500 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/20 transition-all duration-300 shadow-lg hover:shadow-xl text-sm sm:text-base"
              />
              <MagnifyingGlassIcon className="w-5 h-5 sm:w-6 sm:h-6 text-gray-400 absolute left-3 sm:left-5 top-1/2 transform -translate-y-1/2" />
              <button className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-orange-500 hover:bg-orange-600 text-white p-1.5 sm:p-2 rounded-lg sm:rounded-xl transition-colors duration-300 group">
                <ArrowRightIcon className="w-4 h-4 sm:w-5 sm:h-5 group-hover:scale-110 transition-transform" />
              </button>
            </div>
          </div>

          {/* Category Filter Dropdown */}
          <div className="w-full lg:w-64 flex-shrink-0">
            <div className="relative">
              <button
                type="button"
                onClick={() =>
                  setIsCategoryDropdownOpen(!isCategoryDropdownOpen)
                }
                className="w-full appearance-none px-4 sm:px-6 py-3 sm:py-4 pr-10 rounded-xl sm:rounded-2xl border-2 border-gray-200 bg-white/90 backdrop-blur-sm text-gray-700 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/20 transition-all duration-300 shadow-lg hover:shadow-xl text-sm sm:text-base cursor-pointer text-left"
              >
                {expertCategories.find((cat) => cat.id === selectedCategory)
                  ?.name || "All Instructors"}
              </button>
              <ChevronDownIcon
                className={`w-5 h-5 text-gray-400 absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none transition-transform ${isCategoryDropdownOpen ? "rotate-180" : ""}`}
              />

              {isCategoryDropdownOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setIsCategoryDropdownOpen(false)}
                  />
                  <div className="absolute z-20 mt-2 w-full bg-white rounded-xl shadow-lg border border-gray-200 max-h-60 overflow-y-auto">
                    {expertCategories.map((category) => (
                      <button
                        key={category.id}
                        type="button"
                        onClick={() => {
                          setSelectedCategory(category.id);
                          setIsCategoryDropdownOpen(false);
                        }}
                        className={`w-full text-left px-4 py-3 transition-colors border-b border-gray-100 last:border-b-0 ${
                          selectedCategory === category.id
                            ? "bg-orange-50 text-orange-600 font-medium"
                            : "hover:bg-gray-50 text-gray-700 active:bg-gray-100"
                        }`}
                      >
                        {category.name}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Instructor Cards */}
      <div className="container mx-auto px-4 sm:px-6 pb-12 sm:pb-16 zoom-container">
        {/* Instructor Count */}
        {!loading && (
          <div className="mb-4 sm:mb-6">
            <p className="text-gray-600 text-xs sm:text-sm">
              Showing {filteredInstructors.length} of {totalInstructors}{" "}
              instructors
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 sm:justify-items-center">
          {filteredInstructors.map((instructor) => (
            <div key={instructor._id} className="w-full h-full">
              <InstructorCard
                instructor={instructor}
                mobileLayout="horizontal"
              />
            </div>
          ))}
        </div>

        {/* Loading State */}
        {(loading || isFetchingNextPage) && (
          <div className="my-8 flex justify-center">
            <LoadingSpinner
              fullScreen={false}
              showText={true}
              text={
                loading
                  ? "Loading instructors..."
                  : "Loading more instructors..."
              }
            />
          </div>
        )}

        {/* Load More Trigger */}
        <div ref={loadMoreRef} className="h-px" />

        {/* End Message */}
        {!hasNextPage && !loading && filteredInstructors.length > 0 && (
          <div className="mt-8 sm:mt-12 text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r from-orange-400 to-orange-600 rounded-full mb-3 sm:mb-4">
              <svg
                className="w-6 h-6 sm:w-8 sm:h-8 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
              You&apos;ve reached the end!
            </h3>
            <p className="text-sm sm:text-base text-gray-600 mb-4">
              No more instructors to load. Try adjusting your filters to see
              more results.
            </p>
          </div>
        )}

        {/* No Results Message */}
        {!loading &&
          filteredInstructors.length === 0 &&
          instructors.length > 0 && (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                <svg
                  className="w-8 h-8 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
                No instructors found
              </h3>
              <p className="text-sm sm:text-base text-gray-600 mb-4">
                Try adjusting your search terms or filters to find more
                instructors.
              </p>
              <button
                onClick={resetAllFilters}
                className="inline-flex items-center px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors duration-300"
              >
                Clear all filters
              </button>
            </div>
          )}
      </div>

      <style jsx global>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.6s ease-out forwards;
        }

        .animate-slideUp {
          animation: slideUp 0.6s ease-out forwards;
        }

        /* Enhanced mobile responsiveness */
        @media (max-width: 640px) {
          .lg\\:grid-cols-6 {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        /* Glassmorphism effects */
        .backdrop-blur-sm {
          backdrop-filter: blur(12px);
        }

        /* Smooth focus transitions */
        input:focus,
        select:focus {
          transform: translateY(-1px);
          box-shadow: 0 8px 25px -8px rgba(251, 146, 60, 0.3);
        }

        /* Enhanced hover effects */
        .hover\\:scale-\\[1\\.02\\]:hover {
          transform: scale(1.02);
        }

        /* Custom scrollbar */
        ::-webkit-scrollbar {
          width: 6px;
        }

        ::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 10px;
        }

        ::-webkit-scrollbar-thumb {
          background: linear-gradient(45deg, #fb923c, #f97316);
          border-radius: 10px;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(45deg, #f97316, #ea580c);
        }
      `}</style>

      <Footer />
    </div>
  );
};

export default InstructorsPage;
